import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';

import Button from '@/components/Button';
import { useMediaQuery } from '@/hooks';
import ArrowLeft from '@/icons/arrow-left-small.svg';
import ChevronLeft from '@/icons/chevron-left-small.svg';
import Edit from '@/icons/edit.svg';
import DocumentSection from '@/pages/MyDocuments/DocumentSection';
import { EditFolderName } from '@/pages/MyDocuments/FolderList/modals';
import routes from '@/routes';
import { setCurrentFolder } from '@/store/myDocuments';
import { getFolderById } from '@/store/myDocuments/thunks';
import { showError } from '@/utils';

function Folder() {
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const openEditFolderNameModal = () => setIsEditModalOpen(true);
	const dispatch = useDispatch();
	const { id } = useParams();
	const { t } = useTranslation();
	const currentFolder = useSelector(state => state.myDocuments.currentFolder);
	const mdDown = useMediaQuery('mdDown');

	const getButtonSize = () => {
		return mdDown ? 'iconMd' : 'default';
	};

	useEffect(() => {
		if (!currentFolder) {
			dispatch(getFolderById(id)).unwrap().catch(showError);
		}

		return () => {
			dispatch(setCurrentFolder(null));
		};
	}, []);

	const renderHeader = () => (
		<div>
			<Link
				className="group mb-2 flex w-fit items-center gap-1 text-grey-500 transition-colors hover:text-red"
				to={routes.myDocuments}
			>
				<div className="w-2 transition-all group-hover:w-4">
					<ChevronLeft aria-hidden="true" className="group-hover:hidden" />
					<ArrowLeft
						aria-hidden="true"
						className="hidden group-hover:block"
					/>
				</div>
				<span className="text-xs font-semibold">{t('common.back')}</span>
			</Link>
			<div className="flex items-center justify-between">
				<h1 className="font-title text-xl font-semibold leading-8 md:lacks-hover:text-[32px] md:lacks-hover:leading-10 lg:text-[32px] lg:leading-10">
					{currentFolder?.name}
				</h1>

				<Button
					aria-label={t('myDocuments.folders.editName')}
					className="text-grey-200"
					onClick={openEditFolderNameModal}
					variant="outline"
					type="button"
					size={getButtonSize()}
				>
					<Edit aria-hidden="true" className="text-black" />
					<span className="ml-2 hidden text-black md:lacks-hover:block lg:block">
						{t('common.edit')}
					</span>
				</Button>
			</div>
		</div>
	);

	return (
		<div className="flex w-full max-w-[94.5rem] flex-col gap-12 px-8 pt-[56px] md:gap-10">
			{renderHeader()}

			<DocumentSection section="documents" folderId={id} />

			{currentFolder && (
				<EditFolderName
					item={currentFolder}
					onOpenChange={setIsEditModalOpen}
					open={isEditModalOpen}
				/>
			)}
		</div>
	);
}

export default Folder;
